import Highcharts from 'highcharts';

import { colors } from 'styles/theme/colors';

import { NetworkPerformanceEntityRow } from '../useNetworkPerformanceSlideoutAnalytics';

const POINT_WIDTH = 40;

const calculateWidth = (pointWidth: number, dataLength: number) => {
  return (pointWidth + pointWidth * 1.5) * dataLength;
};

export type ScrollConfig = {
  minWidth: number;
  legend: { label: string; color: string };
};

type ColumnChartParams = {
  entityRows: NetworkPerformanceEntityRow[];
  seriesName: string;
  color: string;
  yAxisFormat?: string;
  yAxisAllowDecimals?: boolean;
  tooltipFormatter?: Highcharts.TooltipFormatterCallbackFunction;
};

type ColumnChartResult = {
  options: Highcharts.Options;
  scrollConfig: ScrollConfig;
};

export function buildColumnChartOptions({
  entityRows,
  seriesName,
  color,
  yAxisFormat,
  yAxisAllowDecimals,
  tooltipFormatter,
}: ColumnChartParams): ColumnChartResult {
  const sorted = [...entityRows].sort((a, b) => b.value - a.value);

  return {
    options: {
      chart: {
        type: 'column',
        style: { fontFamily: 'Lato' },
        height: 280,
      },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: { enabled: false },
      xAxis: {
        categories: sorted.map((r) => r.name),
        lineColor: colors.black10,
        tickLength: 0,
        labels: { style: { fontSize: '11px', color: colors.black75 } },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: colors.black10,
        allowDecimals: yAxisAllowDecimals,
        labels: {
          ...(yAxisFormat ? { format: yAxisFormat } : {}),
          style: { fontSize: '11px', color: colors.black50 },
        },
      },
      tooltip: {
        backgroundColor: colors.chartTooltipBackground,
        style: { color: colors.white },
        padding: 12,
        ...(tooltipFormatter ? { formatter: tooltipFormatter } : {}),
      },
      plotOptions: {
        column: {
          borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
          pointWidth: POINT_WIDTH,
        },
        series: { animation: false },
      },
      series: [
        {
          type: 'column',
          name: seriesName,
          data: sorted.map((r) => r.value),
          color,
        },
      ],
    },
    scrollConfig: {
      minWidth: calculateWidth(POINT_WIDTH, sorted.length),
      legend: { label: seriesName, color },
    },
  };
}
